import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Sun, Moon, Calendar as CalIcon } from "lucide-react";
import { Header, Footer } from "@/components/site/Chrome";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { loadIntake, saveIntake, computeEstimate, FALLBACK_CONFIG } from "@/lib/intake";
import { createLead, fetchBlockedDays, fetchEstimateConfig } from "@/lib/api";
import { loadSource } from "@/lib/source";

// Blocked days come from /api/public/blocked-days (admin-configurable).
// We still guard past dates client-side.

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function fmtDate(d) {
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function Book() {
  const navigate = useNavigate();

  const [intake, setIntake] = useState(null);
  const [date, setDate] = useState();
  const [timeWindow, setTimeWindow] = useState(""); // "AM" | "PM"
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [blockedSet, setBlockedSet] = useState(() => new Set());
  const [recurringWeekdays, setRecurringWeekdays] = useState([0, 2]); // Mon+Wed default
  const [estimateConfig, setEstimateConfig] = useState(FALLBACK_CONFIG);

  useEffect(() => {
    let alive = true;
    const today = new Date();
    const to = new Date();
    to.setDate(today.getDate() + 45);
    Promise.all([
      fetchBlockedDays(fmtDate(today), fmtDate(to)),
      fetchEstimateConfig(),
    ])
      .then(([bd, cfg]) => {
        if (!alive) return;
        setBlockedSet(new Set(bd.disabled_dates || []));
        setRecurringWeekdays(bd.recurring_weekdays || []);
        setEstimateConfig(cfg);
      })
      .catch(() => {
        // Fail closed: keep Mon+Wed disabled so we never accidentally allow
        // booking on a day the owner blocks.
        if (!alive) return;
        const fallback = new Set();
        const t = new Date();
        for (let i = 0; i < 46; i++) {
          const d = new Date(t);
          d.setDate(t.getDate() + i);
          const wd = (d.getDay() + 6) % 7; // 0=Mon .. 6=Sun
          if (wd === 0 || wd === 2) fallback.add(fmtDate(d));
        }
        setBlockedSet(fallback);
        setRecurringWeekdays([0, 2]);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const prev = loadIntake();
    if (!prev || !prev.photos?.length || !prev.frequency) {
      toast.error("Please complete your estimate first.");
      navigate("/estimate", { replace: true });
      return;
    }
    setIntake(prev);
    if (prev.booking) {
      if (prev.booking.date) setDate(new Date(prev.booking.date));
      if (prev.booking.window) setTimeWindow(prev.booking.window);
    }
    if (prev.customer) {
      setName(prev.customer.name || "");
      setAddress(prev.customer.address || "");
      setZip(prev.customer.zip || "");
      setPhone(prev.customer.phone || "");
      setNotes(prev.customer.notes || "");
    }
  }, [navigate]);

  // Persist step-4/booking fields to session as user types
  useEffect(() => {
    if (!intake) return;
    saveIntake({
      ...intake,
      booking: { date: date ? fmtDate(date) : "", window: timeWindow },
      customer: { name, address, zip, phone, notes },
    });
  }, [intake, date, timeWindow, name, address, zip, phone, notes]);

  const today = useMemo(() => startOfDay(new Date()), []);
  const thirtyOut = useMemo(() => {
    const d = startOfDay(new Date());
    d.setDate(d.getDate() + 30);
    return d;
  }, []);

  function blockedDaysCopy() {
    if (!recurringWeekdays || recurringWeekdays.length === 0) {
      return "Every day is bookable.";
    }
    const names = ["Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays", "Sundays"];
    const picked = recurringWeekdays.map((i) => names[i]).filter(Boolean);
    if (picked.length === 1) return `${picked[0]} aren't available.`;
    const last = picked[picked.length - 1];
    const rest = picked.slice(0, -1).join(", ");
    return `${rest} and ${last} aren't available.`;
  }

  const disabledDays = [
    { before: today },
    { after: thirtyOut },
    (d) => blockedSet.has(fmtDate(d)),
  ];

  function validate() {
    const e = {};
    if (!date) e.date = "Please pick a date";
    if (!timeWindow) e.window = "Choose AM or PM";
    if (!name.trim()) e.name = "Required";
    if (!address.trim()) e.address = "Required";
    if (!/^\d{5}$/.test(zip)) e.zip = "5-digit zip";
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 10) e.phone = "10-digit US phone";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function onSubmit(ev) {
    ev.preventDefault();
    if (!validate() || !intake) return;
    setSubmitting(true);
    try {
      const payload = {
        photos: intake.photos,
        scope: {
          sidewalk: !!intake.scope.sidewalk,
          deck: !!intake.scope.deck,
          furniture: !!intake.scope.furniture,
          other: !!intake.scope.other,
          other_text: intake.scope.other ? intake.scope.other_text || null : null,
        },
        frequency: intake.frequency,
        booking: {
          preferred_date: fmtDate(date),
          preferred_window: timeWindow,
        },
        customer: {
          name: name.trim(),
          address: address.trim(),
          zip,
          phone: phone.replace(/\D/g, ""),
          notes: notes.trim() || null,
        },
        source: loadSource(),
      };
      const res = await createLead(payload);
      const estimate = computeEstimate(intake.scope, estimateConfig);
      // Stash summary for /success then clear the rest
      sessionStorage.setItem(
        "green-services.success.v1",
        JSON.stringify({
          id: res.id,
          date: fmtDate(date),
          window: timeWindow,
          estimate,
          name: name.trim(),
        })
      );
      navigate("/success");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        (typeof err?.response?.data === "string" ? err.response.data : "") ||
        "Something went wrong submitting your request.";
      console.error("createLead failed", err);
      toast.error(typeof msg === "string" ? msg : "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  }

  if (!intake) return null;

  return (
    <>
      <Header />
      <main className="pt-20 pb-16 min-h-screen">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#334E68] tracking-tight">
            Pick a day &amp; time
          </h1>
          <p className="mt-2 text-sm text-gray-600 leading-relaxed">
            {blockedDaysCopy()} Charlie will text to confirm your slot.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-6">
            {/* Date picker */}
            <div>
              <Label className="text-sm font-medium text-[#334E68] flex items-center gap-2">
                <CalIcon className="w-4 h-4 text-[#16A34A]" /> Preferred date
              </Label>
              <div
                data-testid="date-picker-container"
                className="mt-2 bg-white p-3 sm:p-4 rounded-2xl border border-gray-200 flex justify-center"
              >
                <DayPicker
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={disabledDays}
                  fromDate={today}
                  toDate={thirtyOut}
                  showOutsideDays={false}
                  modifiersClassNames={{
                    selected:
                      "bg-[#16A34A] text-white rounded-lg hover:bg-[#15803D]",
                    today: "font-bold text-[#334E68]",
                  }}
                  classNames={{
                    caption: "flex justify-center py-2 relative items-center",
                    caption_label:
                      "text-base font-semibold font-display text-[#334E68]",
                    nav_button:
                      "h-9 w-9 rounded-lg hover:bg-gray-100 flex items-center justify-center text-[#334E68]",
                    head_cell:
                      "text-gray-500 text-xs font-semibold uppercase w-11 h-9",
                    cell: "w-11 h-11 text-center align-middle",
                    day: "w-11 h-11 rounded-lg hover:bg-gray-100 text-sm font-medium text-[#334E68] transition-colors",
                    day_today: "font-bold",
                  }}
                />
              </div>
              {errors.date && (
                <p className="mt-1 text-xs text-red-600" data-testid="error-date">
                  {errors.date}
                </p>
              )}
              {date && (
                <p
                  className="mt-2 text-sm text-[#334E68] font-medium"
                  data-testid="selected-date-label"
                >
                  Selected:{" "}
                  {date.toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              )}
            </div>

            {/* AM / PM */}
            <div>
              <Label className="text-sm font-medium text-[#334E68]">
                Preferred window
              </Label>
              <div
                role="radiogroup"
                aria-label="Preferred window"
                className="mt-2 grid grid-cols-2 gap-3"
              >
                {[
                  { v: "AM", label: "Morning", sub: "8am – 12pm", Icon: Sun },
                  { v: "PM", label: "Afternoon", sub: "12pm – 5pm", Icon: Moon },
                ].map(({ v, label, sub, Icon }) => {
                  const active = timeWindow === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      data-testid={`window-option-${v}`}
                      onClick={() => setTimeWindow(v)}
                      className={[
                        "min-h-[80px] p-3 rounded-2xl border-2 text-left transition-colors",
                        active
                          ? "border-[#16A34A] bg-green-50/50"
                          : "border-gray-200 bg-white hover:border-gray-300",
                      ].join(" ")}
                    >
                      <Icon
                        className={
                          active
                            ? "w-5 h-5 text-[#16A34A]"
                            : "w-5 h-5 text-gray-400"
                        }
                      />
                      <div className="mt-1 font-display text-base font-semibold text-[#334E68]">
                        {label}
                      </div>
                      <div className="text-xs text-gray-500">{sub}</div>
                    </button>
                  );
                })}
              </div>
              {errors.window && (
                <p className="mt-1 text-xs text-red-600" data-testid="error-window">
                  {errors.window}
                </p>
              )}
            </div>

            {/* Contact fields */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-[#334E68]">
                  Full name
                </Label>
                <Input
                  id="name"
                  data-testid="input-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Alex Rivera"
                  autoComplete="name"
                  className="mt-2 min-h-[52px] rounded-xl border-gray-200 focus:border-[#16A34A] focus:ring-1 focus:ring-[#16A34A]"
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-600" data-testid="error-name">{errors.name}</p>
                )}
              </div>
              <div>
                <Label htmlFor="address" className="text-sm font-medium text-[#334E68]">
                  Hoboken address
                </Label>
                <Input
                  id="address"
                  data-testid="input-address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="123 Washington St"
                  autoComplete="street-address"
                  className="mt-2 min-h-[52px] rounded-xl border-gray-200 focus:border-[#16A34A] focus:ring-1 focus:ring-[#16A34A]"
                />
                {errors.address && (
                  <p className="mt-1 text-xs text-red-600" data-testid="error-address">{errors.address}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="zip" className="text-sm font-medium text-[#334E68]">
                    Zip code
                  </Label>
                  <Input
                    id="zip"
                    data-testid="input-zip"
                    inputMode="numeric"
                    maxLength={5}
                    value={zip}
                    onChange={(e) =>
                      setZip(e.target.value.replace(/\D/g, "").slice(0, 5))
                    }
                    placeholder="07030"
                    autoComplete="postal-code"
                    className="mt-2 min-h-[52px] rounded-xl border-gray-200 focus:border-[#16A34A] focus:ring-1 focus:ring-[#16A34A]"
                  />
                  {errors.zip && (
                    <p className="mt-1 text-xs text-red-600" data-testid="error-zip">{errors.zip}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium text-[#334E68]">
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    data-testid="input-phone"
                    inputMode="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(201) 555-0123"
                    autoComplete="tel"
                    className="mt-2 min-h-[52px] rounded-xl border-gray-200 focus:border-[#16A34A] focus:ring-1 focus:ring-[#16A34A]"
                  />
                  {errors.phone && (
                    <p className="mt-1 text-xs text-red-600" data-testid="error-phone">{errors.phone}</p>
                  )}
                </div>
              </div>
              <div>
                <Label htmlFor="notes" className="text-sm font-medium text-[#334E68]">
                  Notes <span className="text-gray-400 font-normal">(optional)</span>
                </Label>
                <Textarea
                  id="notes"
                  data-testid="input-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Gate code, parking tips, anything Charlie should know…"
                  rows={3}
                  className="mt-2 rounded-xl border-gray-200 focus:border-[#16A34A] focus:ring-1 focus:ring-[#16A34A]"
                />
              </div>
            </div>

            <div className="pt-2 flex flex-col-reverse sm:flex-row gap-3">
              <button
                type="button"
                data-testid="book-back-button"
                onClick={() => navigate("/estimate")}
                className="min-h-[52px] px-6 rounded-2xl text-sm font-medium text-[#334E68] hover:bg-gray-100 flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                type="submit"
                data-testid="confirm-booking-cta"
                disabled={submitting}
                className="flex-1 min-h-[56px] bg-[#16A34A] hover:bg-[#15803D] disabled:bg-gray-300 text-white rounded-2xl font-semibold text-base tracking-wide uppercase flex items-center justify-center gap-2 transition-colors"
              >
                {submitting ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Confirm Booking <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </>
  );
}
